import type { Locale } from '@/i18n/routing'

import { SENTINEL_PATTERN, type Variable } from './icu'

/*
 * Where a value meets the sentence around it, read against the copy's own text with every
 * value still its sentinel — so a value's period, as in `p.m.`, is never the copy's and
 * ends no sentence. Shared by the message renderer and a client template's.
 */

const OPENING_MARKS = `(?:[¡¿«"“‘'(]\\s*)*`
const BLOCK_START = new RegExp(`^\\s*${OPENING_MARKS}$`, 'u')
const SENTENCE_END = new RegExp(`[.!?]["'’”»)]?\\s+${OPENING_MARKS}$`, 'u')

/** Whether a value after `before`, the copy's text since its block opened, starts a sentence:
 *  at the block's start, or after the copy's `.`, `!` or `?`, an optional closing quote or
 *  parenthesis, and whitespace — either way followed by any opening marks (`¡`, `¿`, an
 *  opening quote or parenthesis). */
export const opensSentence = (before: string): boolean =>
    BLOCK_START.test(before) || SENTENCE_END.test(before)

/** A weekday or a month takes its sentence's capital: CLDR forms it mid-sentence, which is
 *  lowercase in many locales. Every other kind keeps the casing it carries. */
export function sentenceCased(
    variable: Variable,
    formatted: string,
    startsSentence: boolean,
    locale: Locale,
): string {
    if (!startsSentence || (variable.kind !== 'weekday' && variable.kind !== 'month')) {
        return formatted
    }
    const [first] = new Intl.Segmenter(locale, { granularity: 'grapheme' }).segment(formatted)
    return first
        ? first.segment.toLocaleUpperCase(locale) + formatted.slice(first.segment.length)
        : formatted
}

/** A sentinel and the copy's period after it, if any. */
const SENTINEL_THEN_PERIOD = new RegExp(`(${SENTINEL_PATTERN.source})(\\.?)`, 'gu')

/** Plain text with each sentinel swapped for its value's text. A value the locale formats
 *  ending in a period (en-CA `11:59 p.m.`, fr `oct.`) takes the copy's sentence-ending period
 *  as its own, so one is written; any other character after it stays. */
export function splicePlain(
    text: string,
    valueText: (sentinel: string, startsSentence: boolean) => string,
): string {
    return text.replace(SENTINEL_THEN_PERIOD, (_, char: string, period: string, offset: number) => {
        const formatted = valueText(char, opensSentence(text.slice(0, offset)))
        return formatted.endsWith('.') ? formatted : formatted + period
    })
}
